import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { Link, useSearchParams } from 'react-router-dom'
import { signUpWithUsernamePasswordV2 } from '../../store/auth/authActions'
import { clearAuth, selectAuthErrors, selectLoginChallenge, selectBlockedLandingPageHtml } from '../../store/auth/authSlice'
import * as React from 'react'
import { useEffect, useState } from 'react'
import MailLogo from '../../assets/images/auth/mail-logo.svg'
import { PendingProvider, SocialButtons, SocialSignupResidencyStep } from './SocialButtons'
import { PasswordForm } from './PasswordForm'
import { Button } from 'primereact/button'
import { EmailInput } from './EmailInput'
import { AuthSeparator } from './Separator'
import { AuthErrorToast } from './AuthErrorToast'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { CaptchaNote } from './CaptchaNote'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'
import { Col, Container, Row } from 'react-bootstrap'
import { Promo } from './Promo'
import { useCompanySso } from '../../common/companySso'
import { encodeState } from './utils'
import './Auth.scss'
import InviteCodeExpired from './errors/InviteCodeExpired'
import InviteCodeExpiredAndResent from './errors/InviteCodeExpiredAndResent'
import useInviteCodeErrors, { AuthError } from './useInviteCodeErrors'
import { LoginHeader } from './headers/LoginHeader'
import BlockedLandingPage from './errors/BlockedLandingPage'
import { ResidencyStep } from './ResidencyStep'
import { DEFAULT_RESIDENCY, Residency } from '../../models/Residency'
import { useResidencySelection } from './useResidencySelection'

const SignUpViewV2: React.FC = () => {
  const dispatch = useAppDispatch()
  const blockedLandingPageHtml = useAppSelector(selectBlockedLandingPageHtml)

  const [emailSignup, setEmailSignup] = useState<boolean>(false)
  const [pendingProvider, setPendingProvider] = useState<PendingProvider | null>(null)
  const residencySelectionEnabled = useResidencySelection()

  const toSocialSignup = () => setEmailSignup(false)
  const toEmailSignup = () => setEmailSignup(true)

  useEffect(() => {
    dispatch(clearAuth())
  }, [dispatch])

  if (blockedLandingPageHtml) {
    return <BlockedLandingPage htmlContent={blockedLandingPageHtml} />
  }

  const signupStep = () => {
    if (emailSignup) {
      return <EmailSignup toSocialSignup={toSocialSignup} />
    }
    if (residencySelectionEnabled && pendingProvider) {
      return <SocialSignupResidencyStep provider={pendingProvider} onBack={() => setPendingProvider(null)} />
    }
    return <SocialSignup toEmailSignup={toEmailSignup} onProviderSelected={setPendingProvider} />
  }

  return (
    <div className='auth-container'>
      <AuthErrorToast />
      <LoginHeader />
      {signupStep()}
    </div>
  )
}

export default SignUpViewV2

const SocialSignup: React.FC<{ toEmailSignup: () => void; onProviderSelected: (provider: PendingProvider) => void }> = ({
  toEmailSignup,
  onProviderSelected,
}) => {
  return (
    <Container className='auth-popup-container'>
      <Row>
        <Col className='p-0 col-xs-12'>
          <div className='auth-popup-inner'>
            <h2 className='auth-popup-header'>Create an Account</h2>
            <SignupButtons toEmailSignup={toEmailSignup} onProviderSelected={onProviderSelected} />
            <SignupFooter />
          </div>
        </Col>
        <Col className='p-0 d-none d-lg-block'>
          <Promo />
        </Col>
      </Row>
    </Container>
  )
}

const SignupButtons: React.FC<{ toEmailSignup: () => void; onProviderSelected: (provider: PendingProvider) => void }> = ({
  toEmailSignup,
  onProviderSelected,
}) => {
  const { shouldUseCompanySso } = useCompanySso()
  return (
    <div className='auth-buttons'>
      <SocialButtons buttonText='Sign up with ' flow='signup' onProviderSelected={onProviderSelected} />
      {!shouldUseCompanySso() && (
        <>
          <AuthSeparator />
          <button className='auth-email-button' onClick={toEmailSignup}>
            <img src={MailLogo} className='auth-button-logo' alt='Email logo' />
            <span className='auth-email-button-text'>Sign up with Email</span>
          </button>
        </>
      )}
    </div>
  )
}

type EmailSignupFormValues = {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword?: string
  state: string | null
  residency: Residency
}

type SignupStep = 'details' | 'residency'

const CAPTCHA_PENDING_MESSAGE = "We're still loading security verification. If this doesn't clear, reload the page and try again."

const EmailSignup: React.FC<{ toSocialSignup: () => void }> = ({ toSocialSignup }) => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const authErrors = useAppSelector(selectAuthErrors)
  const code = searchParams.get('state')
  const bdSource = searchParams.get('bd_source')
  const state = encodeState({ bd_source: bdSource, code })
  const { executeRecaptcha } = useGoogleReCaptcha()
  const residencySelectionEnabled = useResidencySelection()
  const [step, setStep] = useState<SignupStep>('details')
  const [loading, setLoading] = useState<boolean>(false)

  const form = useForm<EmailSignupFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      state,
      residency: DEFAULT_RESIDENCY,
    },
  })
  const { getValues, setValue } = form

  useEffect(() => {
    setValue('state', state)
  }, [setValue, state])

  const onSubmit = async () => {
    if (!executeRecaptcha || loading) {
      return
    }

    setLoading(true)
    try {
      const captchaToken = await executeRecaptcha('signup')
      const { firstName, lastName, email, password, state, residency } = getValues()
      await dispatch(
        signUpWithUsernamePasswordV2({
          firstName,
          lastName,
          email,
          password,
          captchaToken,
          state,
          residency,
        }),
      )
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const loginChallenge = useAppSelector(selectLoginChallenge)
  const { isInviteCodeExpired, isInviteCodeResent } = useInviteCodeErrors(authErrors as AuthError[])

  if (isInviteCodeExpired && !isInviteCodeResent) {
    return <InviteCodeExpired />
  }

  if (isInviteCodeResent) {
    return <InviteCodeExpiredAndResent />
  }

  if (loginChallenge) {
    return (
      <div className='email-signup-container'>
        <div className='auth-popup-container'>
          <div className='auth-popup-inner'>
            <SignupVerification email={getValues('email')} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='email-signup-container'>
      <button className='auth-link-button' onClick={step === 'residency' ? () => setStep('details') : toSocialSignup} disabled={loading} type='button'>
        <span className='icon-chevron-left auth-back-arrow' />
        <span className='auth-link-text'>Back</span>
      </button>
      <div className='auth-popup-container'>
        <div className='auth-popup-inner'>
          <FormProvider {...form}>
            {step === 'details' ? (
              <>
                {residencySelectionEnabled ? (
                  <SignupEmailForm onContinue={() => setStep('residency')} loading={loading} />
                ) : (
                  <SignupEmailForm
                    onContinue={onSubmit}
                    loading={loading}
                    buttonLabel='Create account'
                    disabled={!executeRecaptcha}
                    disabledMessage={CAPTCHA_PENDING_MESSAGE}
                  />
                )}
                <SignupFooter />
              </>
            ) : (
              <SignupResidencyStep onSubmit={onSubmit} loading={loading} captchaReady={!!executeRecaptcha} />
            )}
          </FormProvider>
        </div>
      </div>
    </div>
  )
}

const SignupVerification: React.FC<{ email: string }> = ({ email }) => (
  <>
    <div className='signup-verification-header' />
    <h2 className='auth-popup-header'>Please verify your email</h2>
    <div className='signup-verification-inner'>
      <div className='signup-verification-text'>We have sent you a verification link to</div>
      <div className='signup-verification-email'>{email}</div>
      <div className='signup-verification-text'>Please check your inbox to verify your sign up.</div>
    </div>
    <Link className='signup-verification-footer' to={'/login'}>
      Back to login
    </Link>
  </>
)

const SignupResidencyStep: React.FC<{ onSubmit: () => void; loading: boolean; captchaReady: boolean }> = ({ onSubmit, loading, captchaReady }) => {
  const { control } = useFormContext<EmailSignupFormValues>()

  return (
    <Controller
      name='residency'
      control={control}
      render={({ field }) => (
        <ResidencyStep
          value={field.value}
          onChange={field.onChange}
          onSubmit={onSubmit}
          loading={loading}
          disabled={!captchaReady}
          disabledMessage={CAPTCHA_PENDING_MESSAGE}
        />
      )}
    />
  )
}

const SignupEmailForm: React.FC<{
  onContinue: () => void
  loading: boolean
  buttonLabel?: string
  disabled?: boolean
  disabledMessage?: string
}> = ({ onContinue, loading, buttonLabel = 'Continue', disabled, disabledMessage }) => {
  const { control, handleSubmit, register } = useFormContext<EmailSignupFormValues>()

  return (
    <>
      <h2 className='auth-popup-header'>Create an account</h2>
      <form className='email-signup-form-container' method='post' onSubmit={handleSubmit(onContinue)}>
        <input {...register('state')} type='hidden' />
        <div className='auth-input-field'>
          <label htmlFor='firstName' className='auth-form-label'>
            First name
          </label>
          <Controller
            name='firstName'
            control={control}
            rules={{
              validate: (value) => {
                if (!value) {
                  return 'First name is required.'
                }
                if (!value.trim()) {
                  return 'First name must be at least 1 character.'
                }
                return true
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <InputText
                  placeholder='Enter your first name'
                  disabled={loading}
                  className={classNames('auth-input', { 'auth-input-error': fieldState.error })}
                  id={field.name}
                  {...field}
                />
                {!!fieldState.error && <span className='p-error'>{fieldState.error.message}</span>}
              </>
            )}
          />
        </div>
        <div className='auth-input-field'>
          <label htmlFor='lastName' className='auth-form-label'>
            Last name
          </label>
          <Controller
            name='lastName'
            control={control}
            rules={{
              validate: (value) => {
                if (!value) {
                  return 'Last name is required.'
                }
                if (!value.trim()) {
                  return 'Last name must be at least 1 character.'
                }
                return true
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <InputText
                  placeholder='Enter your last name'
                  disabled={loading}
                  className={classNames('auth-input', { 'auth-input-error': fieldState.error })}
                  id={field.name}
                  {...field}
                />
                {!!fieldState.error && <span className='p-error'>{fieldState.error.message}</span>}
              </>
            )}
          />
        </div>
        <EmailInput loading={loading} />
        <PasswordForm loading={loading} />
        <CaptchaNote />
        <Button className='auth-email-button' type='submit' loading={loading} disabled={disabled}>
          <span className='auth-email-button-text'>{buttonLabel}</span>
        </Button>
        {disabled && disabledMessage && <div className='auth-inline-notice'>{disabledMessage}</div>}
      </form>
    </>
  )
}

const SignupFooter = () => {
  return (
    <div className='auth-footer-text'>
      By creating an account, you agree to our{' '}
      <a href='https://www.zocks.io/terms-of-service' target='_blank' rel='noreferrer'>
        Terms of Service
      </a>{' '}
      and{' '}
      <a href='https://www.zocks.io/privacy-policy' target='_blank' rel='noreferrer'>
        Privacy & Cookie Statement
      </a>
      .
    </div>
  )
}
